'use client';

import { Container } from '@/components/layout/container/container';
import { cn } from '@/lib/utils';
import { HTMLAttributes } from 'react';
import { TypeDocument } from 'types/contentful/TypeDocument';
import { isTypeEmbeddedNote } from 'types/contentful/TypeEmbeddedNote';
import { isTypeHeadingWithTextBlock } from 'types/contentful/TypeHeadingWithTextBlock';
import { EmbeddedNote } from '../../ui/embedded-blocks/embedded-note';
import RichTextViewer from '../../ui/rich-text-viewer/rich-text-viewer';
import TableOfContent, { TableOfContentItem } from '../../ui/table-of-content/table-of-content';
import styles from './document.module.scss';

type ResolvedDocumentFields = TypeDocument<'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'];

export type DocumentProps = {
  document: ResolvedDocumentFields;
} & HTMLAttributes<HTMLElement>;

export const Document = ({ document, className, ...props }: DocumentProps) => {
  const { introduction, isTableOfContents, sections } = document;

  const shouldShowTOC = isTableOfContents && sections && sections.length > 0;

  const tocItems =
    (sections
      ?.map((section, idx) => {
        if (!section || !section.fields || !section.sys || !section.sys.id) {
          return null;
        }
        if (isTypeHeadingWithTextBlock(section)) {
          return {
            label: section.fields.heading || `Section ${idx + 1}`,
            href: `#${section.sys.id}`,
          };
        }
        return null;
      })
      .filter(Boolean) as TableOfContentItem[]) ?? [];

  return (
    <article className={cn(styles.component, className)} {...props}>
      <Container className={styles.component_container}>
        <div className={styles.component_container_content}>
          {introduction && <RichTextViewer content={introduction} />}
          {sections &&
            sections.map((section, index) => (
              <DocumentSection
                key={section?.sys?.id || `malformed-section-${index}`}
                section={section}
              />
            ))}
        </div>
        {shouldShowTOC && (
          <div className={styles.component_container_toc}>
            <TableOfContent items={tocItems} />
          </div>
        )}
      </Container>
    </article>
  );
};

const DocumentSection = ({
  section,
}: {
  section?: NonNullable<ResolvedDocumentFields['sections']>[number];
}) => {
  if (!section?.fields || !section?.sys?.id) return null;

  if (isTypeEmbeddedNote(section)) {
    return (
      <EmbeddedNote
        heading={section.fields.heading}
        content={section.fields.content}
        variant={section.fields.variant}
        inline
      />
    );
  }

  if (isTypeHeadingWithTextBlock(section)) {
    return (
      <section id={section.sys.id} className={styles.component_section}>
        <h2>{section.fields.heading}</h2>
        <RichTextViewer content={section.fields.text} />
      </section>
    );
  }

  return null;
};
